import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useCallback,
  useState,
} from 'react';

import type { Token as Color } from '../../../../export/brand0/colors';

import { FloatingMenuItem as MenuItem } from './item/item';
import { ItemsDisplayMode } from './config';
import { FloatingMenuContextProvider } from './context';
import styles from './floating-menu.scss';
import { ItemsDisplayModeEnumType, Variant } from './types';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    children: React.ReactElement[];
    itemsDisplayMode?: ItemsDisplayModeEnumType;
    color?: string;
    secondaryColor?: string;
    labelColor?: string;
    iconFill?: Color;
    radius?: number;
    align?: string;
    disableOverlay?: boolean;
    dataTestId?: string;
    component?: React.FC;
    variant?: Variant;
  };

const COMPONENT_ID = 'modulor-floating-menu';

export const FloatingMenu: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      itemsDisplayMode = ItemsDisplayMode.IconsTitles,
      color,
      secondaryColor,
      labelColor,
      iconFill,
      radius,
      align,
      disableOverlay,
      component,
      variant = 'primary',
      ...rest
    },
    ref,
    dataTestId = COMPONENT_ID,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleMenu = useCallback((e) => {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }, []);

    const wrapperClasses = classNames(styles.wrapper, {
      [styles.left]: align === 'left',
    });

    const menuClasses = classNames(styles.menu, {
      [styles.open]: isOpen,
    });

    const overlayClasses = classNames(styles.overlay, { [styles.open]: isOpen });

    const isPrimary = variant === 'primary';

    return (
      <FloatingMenuContextProvider itemsDisplayMode={itemsDisplayMode}>
        <div
          data-testid={dataTestId}
          data-id={dataTestId}
          data-component-name={COMPONENT_ID}
          ref={ref}
          className={wrapperClasses}
          {...rest}>
          <div className={menuClasses} data-testid="menu">
            {isOpen && (
              <div className={classNames(styles.list)} onClick={() => setIsOpen(false)}>
                {component &&
                  component({
                    color,
                    labelColor,
                    radius,
                    iconFill,
                    align,
                  })}
                {React.Children.map(children, (child: ReactElement) =>
                  React.cloneElement(child, {
                    style: {
                      width: '100%',
                    },
                    props: {
                      color,
                      labelColor,
                      radius,
                      iconFill,
                      align,
                    },
                  }),
                )}
              </div>
            )}
            <MenuItem
              onClick={toggleMenu}
              color={isPrimary ? secondaryColor : color}
              radius={radius}
              iconFill={iconFill}
              icon={{ name: isOpen ? 'close' : 'menu' }}
              align={align}
              isActionButton={isPrimary}
            />
            {!disableOverlay && isOpen && (
              <div className={overlayClasses} data-testid="overlay" onClick={toggleMenu}></div>
            )}
          </div>
        </div>
      </FloatingMenuContextProvider>
    );
  },
);

FloatingMenu.displayName = 'FloatingMenu';
